import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenStorage';
import { SupportAttachments, AttachmentList } from './SupportAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  open: { label: 'Open', bg: '#dbeafe', fg: '#1e40af' },
  in_progress: { label: 'In Progress', bg: '#fef3c7', fg: '#92400e' },
  awaiting_member: { label: 'Awaiting your reply', bg: '#fed7aa', fg: '#9a3412' },
  resolved: { label: 'Resolved', bg: '#d1fae5', fg: '#065f46' },
  closed: { label: 'Closed', bg: '#f3f4f6', fg: '#374151' },
};

const SupportTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [replyFiles, setReplyFiles] = useState([]);

  const authHeaders = useCallback(() => ({
    withCredentials: true,
    headers: { Authorization: `Bearer ${getToken()}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/support/tickets/me`, authHeaders());
      setTickets(res.data || []);
    } catch (e) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/tickets`, { subject, message, attachments: newFiles }, authHeaders());
      setSubject(''); setMessage(''); setNewFiles([]); setShowForm(false);
      await load();
    } catch (err) {
      alert('Could not submit ticket: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async (ticketId) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, { message: replyText, attachments: replyFiles }, authHeaders());
      setReplyText('');
      setReplyFiles([]);
      await load();
    } catch (err) {
      alert('Reply failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReplying(false);
    }
  };

  const StatusBadge = ({ s }) => {
    const c = statusLabels[s] || { label: s, bg: '#e5e7eb', fg: '#374151' };
    return <span style={{ background: c.bg, color: c.fg, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>{c.label}</span>;
  };

  return (
    <div data-testid="member-support-tab" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Support</h2>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>Open a ticket and we'll get back to you by email.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(s => !s)} data-testid="new-ticket-btn">
          {showForm ? 'Cancel' : '+ New Ticket'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ background: '#f9fafb', padding: '20px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={200} required
            placeholder="Brief summary of your issue"
            style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '12px' }}
            data-testid="ticket-subject-input" />
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Message</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} maxLength={5000} required rows={6}
            placeholder="Please describe what's going on. Include any error messages or screenshots details."
            style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '12px', fontFamily: 'inherit' }}
            data-testid="ticket-message-input" />
          <SupportAttachments attachments={newFiles} setAttachments={setNewFiles} testId="ticket-attach" />
          <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="submit-ticket-btn" style={{ marginTop: '14px' }}>
            {submitting ? 'Submitting…' : 'Submit Ticket'}
          </button>
        </form>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading your tickets…</div>
      ) : tickets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', background: '#f9fafb', borderRadius: '10px' }}>
          You don't have any support tickets yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {tickets.map(t => (
            <div key={t.ticket_id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>{t.subject}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    #{t.ticket_id.slice(-6)} · opened {new Date(t.created_at).toLocaleDateString('en-AU')} · updated {new Date(t.updated_at).toLocaleDateString('en-AU')}
                  </div>
                </div>
                <StatusBadge s={t.status} />
                <button className="btn btn-sm" onClick={() => { setExpanded(expanded === t.ticket_id ? null : t.ticket_id); setReplyText(''); setReplyFiles([]); }}>
                  {expanded === t.ticket_id ? 'Collapse' : 'View'}
                </button>
              </div>

              {expanded === t.ticket_id && (
                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ background: '#f9fafb', padding: '10px 14px', borderRadius: '6px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>You · {new Date(t.created_at).toLocaleString('en-AU')}</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{t.message}</div>
                    <AttachmentList attachments={t.attachments} />
                  </div>
                  {(t.replies || []).map(r => (
                    <div key={r.reply_id} style={{ background: r.author === 'admin' ? '#eff6ff' : '#f9fafb', padding: '10px 14px', borderRadius: '6px', marginBottom: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        {r.author === 'admin' ? `${r.author_name || 'Support'} (Support)` : 'You'} · {new Date(r.created_at).toLocaleString('en-AU')}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{r.message}</div>
                      <AttachmentList attachments={r.attachments} />
                    </div>
                  ))}

                  {t.status !== 'closed' && (
                    <div style={{ marginTop: '12px' }}>
                      <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={3} placeholder="Type your reply…"
                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                        data-testid={`reply-input-${t.ticket_id}`} />
                      <SupportAttachments attachments={replyFiles} setAttachments={setReplyFiles} testId={`reply-attach-${t.ticket_id}`} />
                      <button className="btn btn-primary btn-sm" onClick={() => sendReply(t.ticket_id)} disabled={replying || !replyText.trim()} style={{ marginTop: '8px' }}>
                        {replying ? 'Sending…' : 'Send Reply'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupportTab;
